import { Button, dayRange, isoDay, PageHeader, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Map as MapIcon,
  Pause,
  Table2,
  Timer,
  TriangleAlert,
  UserX,
  Wallet,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { countsQuery, refreshCounts } from "../../counts";
import {
  ATTENTION,
  type AttentionId,
  attentionCounts,
  type BoardRow,
  boardQuery,
  boardSegmentCounts,
  clockOf,
  fleetQueries,
  litresOf,
  SEGMENTS,
  type SegmentId,
  segmentOf,
} from "../../dispatch";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { meQuery } from "../../session";
import { PostpaidDialog } from "../orders/collect";
import styles from "./board.module.css";
import { BoardMap } from "./board-map";
import { FollowPanel } from "./follow-panel";
import { DriversPanel, FillsitePanel } from "./plan-panel";
import { Queue } from "./queue";
import { DispatchTable } from "./table";

export const boardFilters = filterSearch({
  // P8-76: a table over all time by default; the map is one day's
  view: ["table", "map"] as const,
  range: ["all", "day"] as const,
  date: "text",
  segment: [...SEGMENTS.map((s) => s.id), "all" as const],
  geofence_id: "text",
  driver_id: "text",
  // earlier days' deliveries still to plan
  backlog: ["true"] as const,
  // the delivery selected in the list or on the map
  delivery: "text",
  // a delivery, customer, place or plate
  q: "text",
});

/** Waiting for a truck: the side pane plans these, and follows the rest. */
const WAITING = new Set(["pending", "fillsite_assigned"]);
const ICON: Record<AttentionId, typeof Clock> = {
  late: Clock,
  not_accepted: UserX,
  slow: Timer,
  suspended: Pause,
  unpaid: Wallet,
};
const ALL = "all";

/** The table's tabs: the board's segments, then every delivery (P8-76). */
const TABLE_TABS = [...SEGMENTS, { id: "all", label: "All", statuses: [] }] as const;
type TabId = (typeof TABLE_TABS)[number]["id"];

/** How many deliveries match the table's filters in each status, from the API (not the rows loaded). */
const statusCountsQuery = (filters: Record<string, string | undefined>) => ({
  queryKey: ["dispatch-table", "counts", filters],
  staleTime: 15_000,
  queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/status-counts", { params: { query: filters } })),
});

/** "2026-10-18" as a picker's local Date. */
const dayDate = (day: string) => {
  const [y, m, d] = day.split("-");
  return new Date(`${y}-${m}-${d}T00:00:00`);
};
/** "2026-10-18" moved by days. */
const addDays = (day: string, n: number) => {
  // number-ok: a calendar date, not money
  const [y, m, d] = day.split("-").map((x) => Number.parseInt(x, 10));
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + n)).toISOString().slice(0, 10);
};
const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** A delivery, customer, place, plate or driver matches the search. */
const matches = (r: BoardRow, q: string) =>
  [
    r.delivery_number,
    r.order.order_number,
    r.order.customer_name,
    r.location?.label,
    r.geofence?.name,
    r.assignment?.truck?.plate_number,
    r.assignment?.driver?.name,
  ].some((v) => v?.toLowerCase().includes(q));

/**
 * Dispatch (P8-08, redesigned in P8-53 after `Console / Dispatch / Board` and its siblings): the
 * day's deliveries by where they stand, what needs attention, a map of the day, and the selected
 * delivery to plan or follow. Auto-assign and Export aren't here: they have no endpoint yet.
 */
export function DispatchPage(): ReactNode {
  const search = useSearch({ from: "/console/dispatch" });
  const navigate = useNavigate({ from: "/dispatch" });
  const format = useCompanyFormat();
  const today = isoDay(format.timezone);
  const day = search.date ?? today;
  // the map is one day's; the table is all time unless a day is picked. A link to a day, a
  // delivery or the backlog (an order's page, a notification) still opens the day's map.
  const view = search.view ?? (search.date || search.delivery || search.backlog ? "map" : "table");
  const range = view === "map" ? "day" : (search.range ?? "all");
  const tab: TabId = search.segment ?? "unassigned";
  const segment: SegmentId = tab === "all" ? "unassigned" : tab;
  const backlog = view === "map" && search.backlog === "true";
  const board = useQuery(boardQuery(day, { geofence_id: search.geofence_id, driver_id: search.driver_id }, backlog));
  const earlier = useQuery(boardQuery(day, {}, true));
  const drivers = useQuery(fleetQueries.drivers);
  const zones = useQuery(fleetQueries.zones);
  const shifts = useQuery(fleetQueries.shifts);
  const [look, setLook] = useState<AttentionId | null>(null);
  const [changingSite, setChangingSite] = useState<string | null>(null);
  const [postpay, setPostpay] = useState<BoardRow | null>(null);
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQuery);
  const canPostpay = me?.permissions.includes("payments.record") ?? false;
  const counts = useQuery(countsQuery(day));
  const tableFilters = {
    zone: search.geofence_id,
    driver: search.driver_id,
    q: search.q?.trim() || undefined,
    ...(range === "day" ? dayRange({ start: dayDate(day), end: dayDate(day) }, format.timezone) : {}),
  };
  const statusCounts = useQuery({ ...statusCountsQuery(tableFilters), enabled: view === "table" });
  const byStatus = new Map((statusCounts.data?.data ?? []).map((c) => [c.status as string, c.count]));
  const tabCount = (id: TabId) =>
    id === "all"
      ? [...byStatus.values()].reduce((a, b) => a + b, 0)
      : (TABLE_TABS.find((t) => t.id === id)?.statuses ?? []).reduce((a, st) => a + (byStatus.get(st) ?? 0), 0);

  const q = search.q?.trim().toLowerCase() ?? "";
  const all = (board.data?.rows ?? []).filter((r) => !q || matches(r, q));
  const filtered = Boolean(search.geofence_id || search.driver_id || q);
  const segments = boardSegmentCounts(
    all,
    board.data?.complete !== false || filtered || backlog ? undefined : counts.data,
  );
  const needs = attentionCounts(all);
  const lookTest = ATTENTION.find((a) => a.id === look)?.test ?? null;
  const rows = lookTest ? all.filter(lookTest) : all.filter((d) => segmentOf(d.status) === segment);
  const selected = all.find((d) => d.id === search.delivery) ?? null;
  const set = (patch: Partial<typeof search>) => navigate({ search: (s) => ({ ...s, ...patch }) });
  const select = (id: string | undefined) => {
    const row = all.find((d) => d.id === id);
    setChangingSite(null);
    set({ delivery: id, ...(row && !lookTest ? { segment: segmentOf(row.status) } : {}) });
  };
  const waiting = selected ? WAITING.has(selected.status) : false;
  const choosingFillsite = Boolean(
    selected && waiting && litresOf(selected) > 0 && (!selected.assignment?.fillsite || changingSite === selected.id),
  );
  const zoneName = zones.data?.find((z) => z.id === search.geofence_id)?.name ?? null;
  const backlogCount = earlier.data?.rows.length ?? 0;
  const weekday = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${day}T12:00:00Z`));
  const shown = view === "table" ? (statusCounts.data ? tabCount("all") : null) : all.length;
  const subtitle = backlog
    ? `Earlier days · ${all.length} still to plan`
    : [
        range === "all" ? "All time" : day === today ? `Today, ${weekday}` : weekday,
        shown === null ? null : `${shown.toLocaleString("en-GB")} ${shown === 1 ? "delivery" : "deliveries"}`,
        shifts.data ? `${shifts.data.length} ${shifts.data.length === 1 ? "driver" : "drivers"} on shift` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <>
      <PageHeader
        title="Dispatch"
        description={subtitle}
        actions={
          <>
            {view === "map" ? (
              <Button
                variant="secondary"
                // deliveries from earlier days still to plan: in the warning colour, so none are missed
                className={!backlog && backlogCount ? styles.backlogDue : undefined}
                onClick={() => set({ backlog: backlog ? undefined : "true", segment: undefined, delivery: undefined })}
              >
                {backlog ? (
                  "Back to the day"
                ) : backlogCount ? (
                  <>
                    <TriangleAlert size={16} aria-hidden /> {`Backlog · ${backlogCount} from earlier days`}
                  </>
                ) : (
                  "Earlier days"
                )}
              </Button>
            ) : null}
            <Button onClick={() => navigate({ to: "/orders/new" })}>New order</Button>
          </>
        }
      />
      {backlog ? (
        <Alert tone="info" title="Earlier days, still to plan">
          Deliveries for days before today that still have no truck and driver. Plan them, or cancel them on their
          order.
        </Alert>
      ) : null}
      {view === "map" && board.data && !board.data.complete ? (
        <Alert tone="warning" title="Only the first 1,000 deliveries of the day are shown">
          Filter by zone or driver to see the rest.
        </Alert>
      ) : null}
      {board.error ? (
        <Alert tone="danger" title="Couldn't load the board">
          {messageFor(errorOf(board.error))} <TextButton onClick={() => board.refetch()}>Try again</TextButton>
        </Alert>
      ) : null}

      <div className={styles.toolbar}>
        <SegmentedControl
          label="Which days"
          value={range}
          onValueChange={(v) =>
            set(
              v === "all" ? { range: "all", view: "table", backlog: undefined, delivery: undefined } : { range: "day" },
            )
          }
          options={[
            { value: "all", label: "All time" },
            { value: "day", label: "Day" },
          ]}
        />
        {range === "day" ? (
          <div className={`${styles.bare} ${styles.dayStepper}`}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label="The day before"
              disabled={backlog}
              onClick={() => set({ date: addDays(day, -1), delivery: undefined })}
            >
              <ChevronLeft size={16} aria-hidden />
            </button>
            <DatePicker
              label="Day"
              disabled={backlog}
              value={dayDate(day)}
              onChange={(d) => set({ delivery: undefined, date: d ? isoOf(d) : undefined })}
              format={{ weekday: undefined, day: "numeric", month: "short" }}
              showToday
            />
            <button
              type="button"
              className={styles.iconButton}
              aria-label="The day after"
              disabled={backlog}
              onClick={() => set({ date: addDays(day, 1), delivery: undefined })}
            >
              <ChevronRight size={16} aria-hidden />
            </button>
          </div>
        ) : null}
        <div className={styles.bare}>
          <Select
            label="Zone"
            value={search.geofence_id ?? ALL}
            onValueChange={(v) => set({ geofence_id: v === ALL ? undefined : v })}
            options={[
              { value: ALL, label: "All zones" },
              ...(zones.data ?? []).map((z) => ({ value: z.id, label: z.name })),
            ]}
          />
        </div>
        <div className={styles.bare}>
          <Select
            label="Driver"
            value={search.driver_id ?? ALL}
            onValueChange={(v) => set({ driver_id: v === ALL ? undefined : v })}
            // any driver: on shift now or not, for a past day too
            options={[
              { value: ALL, label: "All drivers" },
              ...(drivers.data ?? []).map((d) => ({ value: d.id, label: d.user.name })),
            ]}
          />
        </div>
        <div className={`${styles.bare} ${styles.search}`}>
          <SearchField
            label="Search the board"
            placeholder="Delivery, customer, place or plate"
            value={search.q ?? ""}
            onValueChange={(v) => set({ q: v || undefined })}
          />
        </div>
        <span className={styles.viewSwitch}>
          <SegmentedControl
            label="Show as"
            value={view}
            onValueChange={(v) =>
              set(v === "map" ? { view: "map", range: "day" } : { view: "table", backlog: undefined })
            }
            options={[
              { value: "table", label: "Table", accessory: <Table2 size={14} aria-hidden /> },
              { value: "map", label: "Map", accessory: <MapIcon size={14} aria-hidden /> },
            ]}
          />
        </span>
        {board.dataUpdatedAt ? (
          <span className={styles.live}>
            <span className={styles.liveDot} aria-hidden />
            {`Live · updated ${clockOf(new Date(board.dataUpdatedAt).toISOString(), format.timezone)}`}
          </span>
        ) : null}
      </div>

      <div className={styles.tabsRow}>
        {view === "table" ? (
          <SegmentedControl
            label="Where deliveries stand"
            value={tab}
            onValueChange={(v) => set({ segment: v as TabId, delivery: undefined })}
            options={TABLE_TABS.filter((t) => t.id !== "closed" || tabCount("closed") > 0).map((t) => ({
              value: t.id,
              label: t.label,
              accessory: statusCounts.data ? (
                <span
                  className={styles.count}
                  data-tone={t.id === "unassigned" && tabCount(t.id) ? "warning" : undefined}
                >
                  {tabCount(t.id).toLocaleString("en-GB")}
                </span>
              ) : undefined,
            }))}
          />
        ) : (
          <SegmentedControl
            label="Where deliveries stand"
            value={lookTest ? "" : segment}
            onValueChange={(v) => {
              setLook(null);
              set({ segment: v as SegmentId, delivery: undefined });
            }}
            options={SEGMENTS.filter((s) => s.id !== "closed" || segments.closed > 0).map((s) => ({
              value: s.id,
              label: s.label,
              accessory: board.data ? (
                <span
                  className={styles.count}
                  data-tone={s.id === "unassigned" && segments[s.id] ? "warning" : undefined}
                >
                  {segments[s.id]}
                </span>
              ) : undefined,
            }))}
          />
        )}
        {board.data ? (
          <div className={styles.attentionWrap}>
            <span className={styles.small}>Needs attention</span>
            <ul className={styles.attention} aria-label="Needs attention">
              {ATTENTION.filter((a) => needs[a.id] > 0).map((a) => {
                const Icon = ICON[a.id];
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      className={styles.attentionChip}
                      data-tone={a.tone}
                      aria-pressed={look === a.id}
                      onClick={() => {
                        setLook(look === a.id && view === "map" ? null : a.id);
                        // the chips count the day's board: in the table they open it on the map
                        set({ delivery: undefined, ...(view === "table" ? { view: "map", range: "day" } : {}) });
                      }}
                    >
                      <Icon size={14} aria-hidden /> {`${needs[a.id]} ${a.label}`}
                    </button>
                  </li>
                );
              })}
              {counts.data?.incidents?.open ? (
                <li>
                  <Link to="/incidents" className={styles.attentionChip} data-tone="danger">
                    <TriangleAlert size={14} aria-hidden />{" "}
                    {`${counts.data.incidents.open} open ${counts.data.incidents.open === 1 ? "incident" : "incidents"}`}
                  </Link>
                </li>
              ) : null}
              {ATTENTION.every((a) => needs[a.id] === 0) && !counts.data?.incidents?.open ? (
                <li className={styles.small}>Nothing right now</li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </div>

      {view === "table" ? (
        <DispatchTable
          filters={{
            ...tableFilters,
            status: tab === "all" ? undefined : TABLE_TABS.find((t) => t.id === tab)?.statuses.join(","),
          }}
          filtered={filtered}
        />
      ) : (
        <div className={styles.board}>
          <section
            className={styles.queue}
            aria-label={`Deliveries: ${lookTest ? ATTENTION.find((a) => a.id === look)?.label : SEGMENTS.find((s) => s.id === segment)?.label}`}
          >
            {board.status === "pending" ? (
              <p className={styles.empty}>Loading the day…</p>
            ) : rows.length === 0 ? (
              <div className={styles.empty}>
                <strong>{all.length === 0 && !filtered ? "No deliveries this day" : "Nothing here"}</strong>
                <span className={styles.small}>
                  {all.length === 0 && !filtered
                    ? "Deliveries scheduled for the day, or placed on it without a date, show here."
                    : filtered
                      ? "Clear the search or filters to see the rest of the day."
                      : "Pick another tab to see the rest of the day."}
                </span>
              </div>
            ) : (
              <Queue
                segment={lookTest ? (selected ? segmentOf(selected.status) : "unassigned") : segment}
                title={
                  lookTest ? `${needs[look as AttentionId]} ${ATTENTION.find((a) => a.id === look)?.label}` : undefined
                }
                rows={rows}
                selectedId={selected?.id ?? null}
                onSelect={select}
              />
            )}
          </section>
          <div className={styles.map}>
            <BoardMap
              rows={all}
              segment={segment}
              only={lookTest}
              zone={zoneName}
              selected={selected}
              choosingFillsite={choosingFillsite}
              onSelect={select}
            />
          </div>
          {selected && choosingFillsite ? (
            <FillsitePanel key={selected.id} row={selected} onClose={() => select(undefined)} />
          ) : (selected && waiting) || (!selected && segment === "unassigned") ? (
            <DriversPanel
              rows={all}
              row={selected}
              onChangeFillsite={() => setChangingSite(selected?.id ?? null)}
              onClose={() => select(undefined)}
              onPostpay={canPostpay ? setPostpay : undefined}
            />
          ) : (
            <FollowPanel key={selected?.id ?? "none"} row={selected} onClose={() => select(undefined)} />
          )}
        </div>
      )}
      {postpay ? (
        <PostpaidDialog
          orderId={postpay.order.id}
          orderNumber={postpay.order.order_number}
          onClose={async () => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["dispatch-board"] }),
              refreshCounts(queryClient),
            ]);
            setPostpay(null);
          }}
        />
      ) : null}
    </>
  );
}
